import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { portalApi, formatPortalError } from "@/lib/portalApi";
import { useClientAuth } from "@/context/ClientAuthContext";
import PortalLayout from "@/components/portal/PortalLayout";
import MetricCard from "@/components/portal/MetricCard";
import GrowthChart from "@/components/portal/GrowthChart";
import GrowthCelebration from "@/components/portal/GrowthCelebration";
import Spade from "@/components/Spade";
import { PORTAL } from "@/constants/testIds";
import { LogOut, ExternalLink, Instagram, Hash } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function ClientDashboard() {
    const navigate = useNavigate();
    const { client, logout } = useClientAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await portalApi.get("/portal/dashboard");
                setData(res.data);
            } catch (err) {
                toast.error(formatPortalError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const doLogout = async () => {
        await logout();
        navigate("/portal", { replace: true });
    };

    const m = data?.metrics;
    const name = data?.client?.name || client?.name || "Client";
    const handle = data?.client?.instagram_handle || client?.instagram_handle || "";

    const cards = m
        ? [
              {
                  label: "Estimated Followers",
                  value: m.followers,
                  delta: m.deltas?.followers,
                  tooltip: "The total number of accounts that follow you. The change shows growth since your last weekly update.",
                  testid: PORTAL.cardFollowers,
                  isNumeric: true,
              },
              {
                  label: "Engagement Rate",
                  value: m.engagement_rate,
                  suffix: "%",
                  delta: m.deltas?.engagement_rate,
                  deltaSuffix: "%",
                  tooltip: "How actively your audience interacts (likes, comments, saves) relative to your reach. Higher means a more responsive audience.",
                  testid: PORTAL.cardEngagement,
                  isNumeric: true,
              },
              {
                  label: "Avg Likes / Post",
                  value: m.avg_likes,
                  delta: m.deltas?.avg_likes,
                  tooltip: "The average number of likes each of your posts receives.",
                  testid: PORTAL.cardLikes,
                  isNumeric: true,
              },
              {
                  label: "Avg Comments / Post",
                  value: m.avg_comments,
                  delta: m.deltas?.avg_comments,
                  tooltip: "The average number of comments per post — a strong signal of a genuinely engaged community.",
                  testid: PORTAL.cardComments,
                  isNumeric: true,
              },
              {
                  label: "Estimated Reel Views",
                  value: m.reel_views,
                  delta: m.deltas?.reel_views,
                  tooltip: "How many times your reels have been viewed. Reels are the primary organic reach driver.",
                  testid: PORTAL.cardReels,
                  isNumeric: true,
              },
              {
                  label: "Posting Frequency",
                  value: m.posting_frequency,
                  display: m.posting_frequency || "—",
                  tooltip: "How often content is published each week. Consistency compounds reach over time.",
                  testid: PORTAL.cardFrequency,
                  isNumeric: false,
              },
          ]
        : [];

    return (
        <PortalLayout>
            <GrowthCelebration clientId={data?.client?.id || client?.id} metrics={m} />
            <div data-testid={PORTAL.dashboardRoot} className="min-h-screen">
                {/* Top bar */}
                <header className="sticky top-0 z-30 border-b border-[var(--border-subtle)] bg-[rgba(10,0,0,0.7)] backdrop-blur-xl">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Spade size={22} />
                            <span className="font-display text-lg text-[var(--text-primary)]">
                                Aestherium{" "}
                                <span className="text-[var(--accent-gold)]">Portal</span>
                            </span>
                        </div>
                        <div className="flex items-center gap-3 md:gap-4 text-sm">
                            <a
                                href="/"
                                className="hidden sm:inline-flex items-center gap-1.5 text-[var(--text-secondary)] hover:text-[var(--accent-champagne)] transition-colors"
                            >
                                <ExternalLink size={14} /> Main site
                            </a>
                            <span className="hidden md:inline text-[var(--text-secondary)]">
                                {name}
                            </span>
                            <button
                                data-testid={PORTAL.logout}
                                onClick={doLogout}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--border-subtle)] hover:border-[var(--accent-gold)] transition-colors"
                            >
                                <LogOut size={14} /> Log Out
                            </button>
                        </div>
                    </div>
                </header>

                <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
                    {/* Greeting */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="mb-10 md:mb-14"
                    >
                        <p className="text-xs uppercase tracking-[0.35em] text-[var(--accent-gold)] mb-4">
                            Client Dashboard
                        </p>
                        <h1 className="font-display font-bold text-4xl md:text-6xl leading-[1.02] text-[var(--text-primary)]">
                            Welcome back,{" "}
                            <span className="italic text-[var(--accent-champagne)]">{name}.</span>
                        </h1>
                        <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-[var(--text-secondary)]">
                            {handle && (
                                <span className="inline-flex items-center gap-2">
                                    <Instagram size={15} className="text-[var(--accent-gold)]" />
                                    {handle}
                                </span>
                            )}
                            <span className="inline-flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-gold)]" />
                                Last data update · {formatDate(data?.last_updated)}
                            </span>
                        </div>
                    </motion.div>

                    {loading ? (
                        <p className="text-[var(--text-secondary)] soft-pulse font-display text-xl">
                            Loading your metrics…
                        </p>
                    ) : !m ? (
                        <div className="rounded-2xl surface-3d p-10 text-center">
                            <h2 className="font-display text-2xl text-[var(--text-primary)] mb-2">
                                No metrics yet
                            </h2>
                            <p className="text-[var(--text-secondary)]">
                                Your first weekly report is being prepared. Check back shortly.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Metric cards */}
                            <div
                                data-testid={PORTAL.metricsGrid}
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-10 md:mb-14"
                            >
                                {cards.map((c, i) => (
                                    <MetricCard key={c.testid} index={i} {...c} />
                                ))}
                            </div>

                            {/* Growth chart */}
                            <div className="mb-10 md:mb-14">
                                <GrowthChart history={data.history} />
                            </div>

                            {/* Top hashtags */}
                            <div
                                data-testid={PORTAL.hashtags}
                                data-glow-catch
                                className="rounded-2xl surface-3d p-6 md:p-8"
                            >
                                <div className="flex items-center gap-3 mb-6">
                                    <Hash size={18} className="text-[var(--accent-gold)]" />
                                    <h3 className="font-display text-2xl font-bold text-[var(--text-primary)]">
                                        Top Performing Hashtags
                                    </h3>
                                </div>
                                {data.hashtags.length === 0 ? (
                                    <p className="text-[var(--text-secondary)] text-sm">
                                        No hashtags tracked yet.
                                    </p>
                                ) : (
                                    <div className="flex flex-wrap gap-3">
                                        {data.hashtags.map((t, i) => (
                                            <motion.span
                                                key={t.id}
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                whileInView={{ opacity: 1, scale: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.4, delay: i * 0.05 }}
                                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[var(--border-accent)] bg-[rgba(212,175,55,0.08)] text-[var(--accent-champagne)] text-sm hover:bg-[rgba(212,175,55,0.15)] transition-colors"
                                            >
                                                {t.hashtag}
                                            </motion.span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </main>
            </div>
        </PortalLayout>
    );
}
